import React, { Fragment, useContext, useState } from "react";
import { Button } from "@mui/material";
import { EssealDataTable } from "esseal-data-table";
import subContractorsColumns from "./subContractorsColumns";
import { useMutation } from "@apollo/client/react";
import { Edit as EditIcon } from "@mui/icons-material";
import { CompanyResource, CreateSubContractorInvitationDocument, CompanyResourceResourceTypeEnum, PermissionActionEnum, PermissionEntityEnum } from "@/graphql/generated/graphql";
import CreateCompanyResourceModal from "@/components/CompanyResource/CreateCompanyResourceModal";
import PermissionContext from "@/Contexts/Permissions";
import RequestPermissionTooltip from "@/components/RequestPermissionTooltip";

interface SubContractorsTableProps {
  loading: boolean;
  data: CompanyResource[];
}

const SubContractorsTable: React.FC<SubContractorsTableProps> = ({
  data,
  loading,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [createInvitation] = useMutation(CreateSubContractorInvitationDocument);
  const [selectedResource, setSelectedResource] = useState<CompanyResource | undefined>(undefined);

  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const { Create, Manage } = PermissionActionEnum;
  const { SubcontractorManagement } = PermissionEntityEnum;
  const hasCreatePermission = canPerformAction(Create, SubcontractorManagement);
  const hasManagePermission = canPerformAction(Manage, SubcontractorManagement);

  const handleCreateInvitation = (id: string) => {
    createInvitation({
      variables: { companyResourceId: id }
    })
  }

  return (
    <Fragment>
      <EssealDataTable
        rows={data}
        columns={subContractorsColumns(handleCreateInvitation)}
        loading={loading}
        checkboxSelection={false}
        pagination={true}
        pageSize={25}

        rowActions={(row) => [
          {
            label: 'Edit',
            icon: <EditIcon fontSize="small" color={hasManagePermission ? "primary" : "disabled"} />,
            tooltip: 'Edit SubContractor Resource',
            disabled: !hasManagePermission,
            onClick: () => {
              if (!hasManagePermission) return;
              setSelectedResource(row);
              setShowCreateModal(true);
            }
          },
        ]}
        toolbar={
          <RequestPermissionTooltip
            hasPermission={hasCreatePermission}
            requiredPermission={{ action: Create, entity: SubcontractorManagement }}
            checkRequestExists={checkRequestExists}
            onRequest={requestPermission}
          >
            <Button
              disabled={!hasCreatePermission}
              onClick={() => setShowCreateModal(true)}
            >
              + Add New
            </Button>
          </RequestPermissionTooltip>
        }
      />

      {showCreateModal && (
        <CreateCompanyResourceModal
          handleClose={() => {
            setSelectedResource(undefined);
            setShowCreateModal(false);
          }}
          selectedResource={selectedResource}
          resourceType={CompanyResourceResourceTypeEnum.SubContractor}
        />
      )}
    </Fragment>
  );
};

export default SubContractorsTable;
